import { useCallback, useEffect, useRef, useState } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SchoolForm from './pages/SchoolForm';
import SchoolDetail from './pages/SchoolDetail';
import SchoolDashboard from './pages/SchoolDashboard';
import DismissalConsole from './pages/DismissalConsole';
import PickupZonesPage from './pages/PickupZonesPage';
import ParentLocationDiagnosticsPage from './pages/ParentLocationDiagnosticsPage';
import CalendarPage from './pages/CalendarPage';
import SchoolMessagesPage from './pages/SchoolMessagesPage';
import PushNotificationsPage from './pages/PushNotificationsPage';
import SystemPreferencesPage from './pages/SystemPreferencesPage';
import StaffList from './pages/StaffList';
import StaffForm from './pages/StaffForm';
import StudentList from './pages/StudentList';
import StudentForm from './pages/StudentForm';
import ParentList from './pages/ParentList';
import DriverList from './pages/DriverList';
import ParentForm from './pages/ParentForm';
import ParentDetail from './pages/ParentDetail';
import BusManagementPage from './pages/BusManagementPage';
import ResetPassword from './pages/ResetPassword';
import Simulate from './pages/Simulate';
import ServerHealth from './pages/ServerHealth';
import CarpoolReplayPage from './pages/CarpoolReplayPage';
import CarpoolSchedulePage from './pages/CarpoolSchedulePage';
import VisitorsPage from './pages/VisitorsPage';
import FieldTripFormsPage from './pages/FieldTripFormsPage';
import ExpoPreviews from './pages/ExpoPreviews';
import CsvImport from './pages/CsvImport';
import ImportReviewPage from './pages/ImportReviewPage';
import DismissalStateControl from './components/DismissalStateControl';
import SchoolLayout from './components/SchoolLayout';
import { apiFetch } from './config/api';
import { clearStoredUser, loadStoredUser, saveStoredUser } from './lib/authStorage';
import { getSchoolAdminSchoolId, isSuperAdminUser } from './lib/adminAccess';
import { prepareIdScanner, releaseIdScanner, subscribeIdScanner } from './lib/idScanner';
import { hasLiveDismissalActivity } from './lib/dismissalData';
import { endRealDismissal, startRealDismissal } from './lib/dismissalLifecycle';
import './App.css';

const DEFAULT_ROUTE = { page: 'dashboard', pageData: null };

// Debug flag — toggles the header dismissal status into a clickable button so
// devs can flip the LIVE/INACTIVE state by hand. Off by default in production.
// Flip to `true` here, or run `localStorage.setItem('debug.dismissalToggle', '1')`
// in DevTools to enable without a rebuild.
const DEBUG_DISMISSAL_TOGGLE = false;
const dismissalToggleInteractive = DEBUG_DISMISSAL_TOGGLE
  || (typeof window !== 'undefined' && window.localStorage?.getItem('debug.dismissalToggle') === '1');
const SCHOOL_ADMIN_DEFAULT_PAGE = 'school-dashboard';
const SCHOOL_ADMIN_ALLOWED_PAGES = new Set([
  'school-dashboard',
  'dismissal-console',
  'pickup-zones',
  'messages',
  'push-notifications',
  'calendar',
  'carpool-schedule',
  'settings',
  'parents',
  'drivers',
  'staff',
  'visitors',
  'field-trip-forms',
  'students',
  'buses',
  'import-review',
  'staff-create',
  'staff-edit',
  'student-create',
  'student-edit',
  'parent-create',
  'parent-detail',
  'parent-edit',
  'driver-edit',
]);

function schoolIdFromRoute(pageData) {
  if (typeof pageData === 'number') return pageData;
  const schoolId = Number(pageData?.schoolId);
  return Number.isInteger(schoolId) && schoolId > 0 ? schoolId : null;
}

function toInt(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function normalizePageData(target, data = null) {
  switch (target) {
    case 'reset-password':
      return {
        code: typeof data === 'string'
          ? data.trim().toUpperCase()
          : (typeof data?.code === 'string' ? data.code.trim().toUpperCase() : ''),
      };
    case 'school-dashboard':
    case 'dismissal-console':
    case 'school-detail':
    case 'calendar':
    case 'carpool-schedule':
    case 'settings':
    case 'gps-diagnostics':
    case 'messages':
    case 'push-notifications':
    case 'import-review':
    case 'student-create':
    case 'parent-create':
    case 'staff-create':
    case 'buses':
      return typeof data === 'number' ? data : toInt(data?.schoolId ?? data);
    case 'visitors': {
      if (typeof data === 'number') {
        return { schoolId: data, selectVisitId: null };
      }
      return {
        schoolId: toInt(data?.schoolId ?? data),
        selectVisitId: toInt(data?.selectVisitId) || null,
      };
    }
    case 'field-trip-forms':
      return typeof data === 'number' ? data : toInt(data?.schoolId ?? data);
    case 'pickup-zones':
      if (typeof data === 'number') {
        return { schoolId: data, focusBuilder: false };
      }
      return {
        schoolId: toInt(data?.schoolId ?? data),
        focusBuilder: Boolean(data?.focusBuilder),
      };
    case 'staff': {
      if (typeof data === 'number') {
        return { schoolId: data, selectStaffId: null };
      }
      return {
        schoolId: toInt(data?.schoolId ?? data),
        selectStaffId: toInt(data?.selectStaffId) || null,
      };
    }
    case 'students': {
      if (typeof data === 'number') {
        return { schoolId: data, selectStudentId: null };
      }
      return {
        schoolId: toInt(data?.schoolId ?? data),
        selectStudentId: toInt(data?.selectStudentId) || null,
      };
    }
    case 'parents': {
      if (typeof data === 'number') {
        return { schoolId: data, selectParentId: null };
      }
      return {
        schoolId: toInt(data?.schoolId ?? data),
        selectParentId: toInt(data?.selectParentId) || null,
      };
    }
    case 'drivers': {
      if (typeof data === 'number') {
        return { schoolId: data, selectDriverId: null };
      }
      return {
        schoolId: toInt(data?.schoolId ?? data),
        selectDriverId: toInt(data?.selectDriverId) || null,
      };
    }
    case 'school-edit': {
      const school = data?.school || (data && typeof data === 'object' && 'legal_name' in data ? data : null);
      return {
        schoolId: toInt(data?.schoolId ?? school?.id ?? data?.id ?? data),
        school: school || null,
      };
    }
    case 'staff-edit': {
      const staff = data?.staff || (data && typeof data === 'object' && 'permission_level' in data ? data : null);
      return {
        schoolId: toInt(data?.schoolId ?? staff?.school_id),
        staffId: toInt(data?.staffId ?? staff?.id),
        staff: staff || null,
      };
    }
    case 'student-edit': {
      const student = data?.student || (data && typeof data === 'object' && 'grade' in data ? data : null);
      return {
        schoolId: toInt(data?.schoolId ?? student?.school_id),
        studentId: toInt(data?.studentId ?? student?.id),
        student: student || null,
      };
    }
    case 'parent-edit': {
      const parent = data?.parent || (data && typeof data === 'object' && 'license_plate' in data ? data : null);
      return {
        schoolId: toInt(data?.schoolId ?? parent?.school_ids?.[0]),
        parentId: toInt(data?.parentId ?? parent?.id),
        parent: parent || null,
      };
    }
    case 'driver-edit': {
      const driver = data?.driver || (data && typeof data === 'object' && data.type === 'driver' ? data : null);
      return {
        schoolId: toInt(data?.schoolId ?? driver?.school_ids?.[0]),
        driverId: toInt(data?.driverId ?? driver?.id),
        driver: driver || null,
      };
    }
    case 'parent-detail':
      return {
        schoolId: toInt(data?.schoolId),
        parentId: toInt(data?.parentId),
      };
    case 'school-create':
    case 'simulate':
    case 'carpool-replay':
    case 'server-health':
    case 'expo-previews':
    case 'csv-import':
    case 'dashboard':
    default:
      return null;
  }
}

function buildRoute(target, rawData = null) {
  return {
    page: target,
    pageData: normalizePageData(target, rawData),
  };
}

function buildPath(target, rawData = null) {
  const data = normalizePageData(target, rawData);

  switch (target) {
    case 'reset-password': {
      const query = data?.code ? `?code=${encodeURIComponent(data.code)}` : '';
      return `/reset-password${query}`;
    }
    case 'dashboard':
      return '/dashboard';
    case 'simulate':
      return '/simulate';
    case 'carpool-replay':
      return '/carpool-replay';
    case 'carpool-schedule':
      return data ? `/schools/${data}/carpool-schedule` : '/dashboard';
    case 'server-health':
      return '/server-health';
    case 'expo-previews':
      return '/expo-previews';
    case 'csv-import':
      return '/csv-import';
    case 'school-create':
      return '/schools/new';
    case 'school-detail':
      return data ? `/schools/${data}` : '/dashboard';
    case 'school-dashboard':
      return data ? `/schools/${data}/dashboard` : '/dashboard';
    case 'dismissal-console':
      return data ? `/schools/${data}/dismissal` : '/dashboard';
    case 'pickup-zones':
      return data?.schoolId ? `/schools/${data.schoolId}/pickup-zones` : '/dashboard';
    case 'gps-diagnostics':
      return data ? `/schools/${data}/gps-diagnostics` : '/dashboard';
    case 'messages':
      return data ? `/schools/${data}/messages` : '/dashboard';
    case 'push-notifications':
      return data ? `/schools/${data}/push-notifications` : '/dashboard';
    case 'calendar':
      return data ? `/schools/${data}/calendar` : '/dashboard';
    case 'settings':
      return data ? `/schools/${data}/settings` : '/dashboard';
    case 'staff':
      return data?.schoolId ? `/schools/${data.schoolId}/staff` : '/dashboard';
    case 'buses':
      return data ? `/schools/${data}/buses` : '/dashboard';
    case 'visitors':
      return data?.schoolId ? `/schools/${data.schoolId}/visitors` : '/dashboard';
    case 'field-trip-forms':
      return data ? `/schools/${data}/field-trip-forms` : '/dashboard';
    case 'school-edit':
      return data?.schoolId ? `/schools/${data.schoolId}/edit` : '/dashboard';
    case 'students':
      return data?.schoolId ? `/schools/${data.schoolId}/students` : '/dashboard';
    case 'import-review':
      return data ? `/schools/${data}/import-review` : '/dashboard';
    case 'student-create':
      return data ? `/schools/${data}/students/new` : '/dashboard';
    case 'student-edit':
      return data?.schoolId && data?.studentId
        ? `/schools/${data.schoolId}/students/${data.studentId}/edit`
        : '/dashboard';
    case 'parents':
      return data?.schoolId ? `/schools/${data.schoolId}/parents` : '/dashboard';
    case 'drivers':
      return data?.schoolId ? `/schools/${data.schoolId}/drivers` : '/dashboard';
    case 'parent-create':
      return data ? `/schools/${data}/parents/new` : '/dashboard';
    case 'parent-detail':
      return data?.schoolId && data?.parentId
        ? `/schools/${data.schoolId}/parents/${data.parentId}`
        : '/dashboard';
    case 'parent-edit':
      return data?.schoolId && data?.parentId
        ? `/schools/${data.schoolId}/parents/${data.parentId}/edit`
        : '/dashboard';
    case 'driver-edit':
      return data?.schoolId && data?.driverId
        ? `/schools/${data.schoolId}/drivers/${data.driverId}/edit`
        : '/dashboard';
    case 'staff-create':
      return data ? `/schools/${data}/staff/new` : '/dashboard';
    case 'staff-edit':
      return data?.schoolId && data?.staffId
        ? `/schools/${data.schoolId}/staff/${data.staffId}/edit`
        : '/dashboard';
    default:
      return '/dashboard';
  }
}

function parseRoutePath(path) {
  const [rawPath, rawQuery = ''] = String(path || '').split('?');
  const query = new URLSearchParams(rawQuery);
  const cleaned = rawPath
    .replace(/^#/, '')
    .replace(/^\/+/, '');

  if (cleaned === 'reset-password') {
    return {
      page: 'reset-password',
      pageData: {
        code: (query.get('code') || '').trim().toUpperCase(),
      },
    };
  }

  if (!cleaned || cleaned === 'dashboard') {
    return DEFAULT_ROUTE;
  }

  const segments = cleaned.split('/').filter(Boolean);

  if (segments[0] === 'simulate') {
    return { page: 'simulate', pageData: null };
  }

  if (segments[0] === 'carpool-replay') {
    return { page: 'carpool-replay', pageData: null };
  }

  if (segments[0] === 'server-health') {
    return { page: 'server-health', pageData: null };
  }

  if (segments[0] === 'expo-previews') {
    return { page: 'expo-previews', pageData: null };
  }

  if (segments[0] === 'csv-import') {
    return { page: 'csv-import', pageData: null };
  }

  if (segments[0] !== 'schools') {
    return DEFAULT_ROUTE;
  }

  if (segments[1] === 'new') {
    return { page: 'school-create', pageData: null };
  }

  const schoolId = toInt(segments[1]);
  if (!schoolId) {
    return DEFAULT_ROUTE;
  }

  if (segments.length === 2) {
    return { page: 'school-detail', pageData: schoolId };
  }

  switch (segments[2]) {
    case 'dashboard':
      return { page: 'school-dashboard', pageData: schoolId };
    case 'dismissal':
      return { page: 'dismissal-console', pageData: schoolId };
    case 'pickup-zones':
      return { page: 'pickup-zones', pageData: schoolId };
    case 'gps-diagnostics':
      return { page: 'gps-diagnostics', pageData: schoolId };
    case 'messages':
      return { page: 'messages', pageData: schoolId };
    case 'push-notifications':
      return { page: 'push-notifications', pageData: schoolId };
    case 'calendar':
      return { page: 'calendar', pageData: schoolId };
    case 'carpool-schedule':
      return { page: 'carpool-schedule', pageData: schoolId };
    case 'settings':
      return { page: 'settings', pageData: schoolId };
    case 'buses':
      return { page: 'buses', pageData: schoolId };
    case 'visitors':
      return { page: 'visitors', pageData: schoolId };
    case 'field-trip-forms':
      return { page: 'field-trip-forms', pageData: schoolId };
    case 'import-review':
      return { page: 'import-review', pageData: schoolId };
    case 'edit':
      return { page: 'school-edit', pageData: { schoolId } };
    case 'students': {
      if (segments[3] === 'new') {
        return { page: 'student-create', pageData: schoolId };
      }
      const studentId = toInt(segments[3]);
      if (studentId && segments[4] === 'edit') {
        return { page: 'student-edit', pageData: { schoolId, studentId } };
      }
      return { page: 'students', pageData: { schoolId, selectStudentId: null } };
    }
    case 'parents': {
      if (segments[3] === 'new') {
        return { page: 'parent-create', pageData: schoolId };
      }
      const parentId = toInt(segments[3]);
      if (parentId && segments[4] === 'edit') {
        return { page: 'parent-edit', pageData: { schoolId, parentId } };
      }
      if (parentId) {
        return { page: 'parent-detail', pageData: { schoolId, parentId } };
      }
      return { page: 'parents', pageData: { schoolId, selectParentId: null } };
    }
    case 'drivers': {
      const driverId = toInt(segments[3]);
      if (driverId && segments[4] === 'edit') {
        return { page: 'driver-edit', pageData: { schoolId, driverId } };
      }
      return { page: 'drivers', pageData: { schoolId, selectDriverId: null } };
    }
    case 'staff': {
      if (segments.length === 3) {
        return { page: 'staff', pageData: { schoolId, selectStaffId: null } };
      }
      if (segments[3] === 'new') {
        return { page: 'staff-create', pageData: schoolId };
      }
      const staffId = toInt(segments[3]);
      if (staffId && segments[4] === 'edit') {
        return { page: 'staff-edit', pageData: { schoolId, staffId } };
      }
      return { page: 'school-detail', pageData: schoolId };
    }
    default:
      return { page: 'school-detail', pageData: schoolId };
  }
}

function getRouteFromBrowser() {
  if (typeof window === 'undefined') {
    return DEFAULT_ROUTE;
  }

  const sourcePath = window.location.hash?.startsWith('#/')
    ? window.location.hash
    : `${window.location.pathname}${window.location.search}`;
  const parsedRoute = parseRoutePath(sourcePath);
  const historyRoute = window.history.state?.route;

  if (!historyRoute?.page) {
    return parsedRoute;
  }

  const normalizedRoute = {
    page: historyRoute.page,
    pageData: normalizePageData(historyRoute.page, historyRoute.pageData),
  };

  return buildPath(normalizedRoute.page, normalizedRoute.pageData) === buildPath(parsedRoute.page, parsedRoute.pageData)
    ? normalizedRoute
    : parsedRoute;
}

function getRouteSchoolId(route) {
  switch (route.page) {
    case 'school-dashboard':
    case 'dismissal-console':
    case 'pickup-zones':
    case 'gps-diagnostics':
    case 'messages':
    case 'push-notifications':
    case 'calendar':
    case 'carpool-schedule':
    case 'settings':
    case 'import-review':
    case 'parent-create':
    case 'staff-create':
    case 'student-create':
    case 'buses':
    case 'visitors':
      return toInt(route.pageData?.schoolId ?? route.pageData);
    case 'field-trip-forms':
      return toInt(route.pageData?.schoolId ?? route.pageData);
    case 'staff':
    case 'students':
    case 'parents':
    case 'drivers':
      return toInt(route.pageData?.schoolId ?? route.pageData);
    case 'staff-edit':
    case 'student-edit':
    case 'parent-detail':
    case 'parent-edit':
    case 'driver-edit':
      return toInt(route.pageData?.schoolId);
    default:
      return null;
  }
}

function getSchoolAdminDefaultRoute(user) {
  const schoolId = getSchoolAdminSchoolId(user);
  return schoolId
    ? { page: SCHOOL_ADMIN_DEFAULT_PAGE, pageData: schoolId }
    : DEFAULT_ROUTE;
}

function getAccessibleRoute(user, route) {
  const normalizedRoute = buildRoute(route.page, route.pageData);

  if (!user || isSuperAdminUser(user)) {
    return normalizedRoute;
  }

  const schoolId = getSchoolAdminSchoolId(user);
  if (!schoolId) {
    return DEFAULT_ROUTE;
  }

  if (!SCHOOL_ADMIN_ALLOWED_PAGES.has(normalizedRoute.page)) {
    return getSchoolAdminDefaultRoute(user);
  }

  return getRouteSchoolId(normalizedRoute) === schoolId
    ? normalizedRoute
    : getSchoolAdminDefaultRoute(user);
}

function routesMatch(left, right) {
  return buildPath(left.page, left.pageData) === buildPath(right.page, right.pageData);
}

export default function App() {
  const [user, setUser] = useState(() => loadStoredUser());
  const [route, setRoute] = useState(() => getRouteFromBrowser());
  const routeRef = useRef(route);
  routeRef.current = route;
  const schoolRef = useRef(null);
  const routedSchoolId = schoolIdFromRoute(route?.pageData) || getSchoolAdminSchoolId(user);
  if (routedSchoolId) schoolRef.current = routedSchoolId;
  const [dismissalActiveBySchool, setDismissalActiveBySchool] = useState({});

  const scopedSchoolId = user ? getSchoolAdminSchoolId(user) : null;
  const accessibleRoute = user ? getAccessibleRoute(user, route) : route;
  const page = accessibleRoute.page;
  const pageData = accessibleRoute.pageData;
  const schoolShellId = ['school-dashboard', 'dismissal-console', 'pickup-zones', 'gps-diagnostics', 'messages', 'push-notifications', 'calendar', 'carpool-schedule', 'settings', 'parents', 'drivers', 'staff', 'students', 'import-review']
    .includes(page)
    ? getRouteSchoolId(accessibleRoute)
    : null;

  const setBrowserRoute = useCallback((target, data = null, { replace = false } = {}) => {
    const nextRoute = buildRoute(target, data);

    setRoute(nextRoute);

    if (typeof window !== 'undefined') {
      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({ route: nextRoute }, '', buildPath(nextRoute.page, nextRoute.pageData));
    }
  }, []);

  const navigate = useCallback((target, data = null, options = {}) => {
    const nextRoute = buildRoute(target, data);
    const accessibleNextRoute = user ? getAccessibleRoute(user, nextRoute) : nextRoute;
    setBrowserRoute(accessibleNextRoute.page, accessibleNextRoute.pageData, options);
  }, [setBrowserRoute, user]);

  const getDismissalActive = useCallback(
    (schoolId) => (schoolId ? dismissalActiveBySchool[schoolId] ?? false : false),
    [dismissalActiveBySchool]
  );

  const setDismissalActive = useCallback((schoolId, nextValue) => {
    if (!schoolId) return;

    setDismissalActiveBySchool((current) => ({
      ...current,
      [schoolId]: typeof nextValue === 'function'
        ? nextValue(current[schoolId] ?? false)
        : nextValue,
    }));
  }, []);

  const syncDismissalActiveFromBackend = useCallback(async (schoolId) => {
    if (!schoolId || !user?.token) return;

    const [dismissalRes, geofenceRes] = await Promise.allSettled([
      apiFetch(`/dismissal-status?school_id=${schoolId}`, {}, user.token),
      apiFetch(`/geofenced-data/school/${schoolId}`, {}, user.token),
    ]);

    setDismissalActive(schoolId, hasLiveDismissalActivity({
      dismissalStatuses: dismissalRes.status === 'fulfilled' ? dismissalRes.value.data || [] : [],
      geofenceEvents: geofenceRes.status === 'fulfilled' ? geofenceRes.value.data || [] : [],
    }));
  }, [setDismissalActive, user?.token]);

  const handleStartDismissal = useCallback(async (schoolId) => {
    if (!schoolId || !user?.token) return;

    try {
      await startRealDismissal({ schoolId, token: user.token });
    } catch (error) {
      console.error('Failed to start dismissal:', error);
    } finally {
      try {
        await syncDismissalActiveFromBackend(schoolId);
      } catch (error) {
        console.error('Failed to sync dismissal state:', error);
      }
    }
  }, [syncDismissalActiveFromBackend, user?.token]);

  const handleEndDismissal = useCallback(async (schoolId) => {
    if (!schoolId || !user?.token) return;

    try {
      await endRealDismissal({ schoolId, token: user.token });
    } catch (error) {
      console.error('Failed to end dismissal:', error);
    } finally {
      try {
        await syncDismissalActiveFromBackend(schoolId);
      } catch (error) {
        console.error('Failed to sync dismissal state:', error);
      }
    }
  }, [syncDismissalActiveFromBackend, user?.token]);

  const handleDismissalToggle = useCallback((schoolId) => {
    if (getDismissalActive(schoolId)) {
      handleEndDismissal(schoolId);
      return;
    }

    handleStartDismissal(schoolId);
  }, [getDismissalActive, handleEndDismissal, handleStartDismissal]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const currentRoute = getRouteFromBrowser();
    setRoute(currentRoute);
    window.history.replaceState({ route: currentRoute }, '', buildPath(currentRoute.page, currentRoute.pageData));

    const handlePopState = () => {
      setRoute(getRouteFromBrowser());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const seenScanId = { current: '' };
    const stop = subscribeIdScanner((scanner) => {
      if (!scanner.lastScan || scanner.lastScan.id === seenScanId.current) return;
      seenScanId.current = scanner.lastScan.id;
      const current = routeRef.current;
      if (current?.page === 'visitors') return;
      const schoolId = schoolIdFromRoute(current?.pageData) || schoolRef.current || getSchoolAdminSchoolId(user);
      if (!schoolId) return;
      navigate('visitors', { schoolId });
    });
    prepareIdScanner();
    return () => {
      stop();
      releaseIdScanner();
    };
  }, [navigate, user]);

  useEffect(() => {
    if (!user) return;

    const nextRoute = getAccessibleRoute(user, route);
    if (!routesMatch(route, nextRoute)) {
      setBrowserRoute(nextRoute.page, nextRoute.pageData, { replace: true });
    }
  }, [route, setBrowserRoute, user]);

  useEffect(() => {
    if (!user || !schoolShellId) return;
    syncDismissalActiveFromBackend(schoolShellId);
  }, [schoolShellId, syncDismissalActiveFromBackend, user]);

  const handleLogin = useCallback((userRecord) => {
    saveStoredUser(userRecord);
    setUser(userRecord);
    const targetRoute = getSchoolAdminSchoolId(userRecord)
      ? getSchoolAdminDefaultRoute(userRecord)
      : getAccessibleRoute(userRecord, getRouteFromBrowser());
    setBrowserRoute(targetRoute.page, targetRoute.pageData, { replace: true });
  }, [setBrowserRoute]);

  const handleLogout = useCallback(() => {
    clearStoredUser();
    setBrowserRoute('dashboard', null, { replace: true });
    setDismissalActiveBySchool({});
    setUser(null);
  }, [setBrowserRoute]);

  if (!user) {
    if (route.page === 'reset-password') {
      return (
        <ResetPassword
          initialCode={route.pageData?.code || ''}
          onBack={() => setBrowserRoute('dashboard', null, { replace: true })}
        />
      );
    }
    return <Login onLogin={handleLogin} onReset={() => setBrowserRoute('reset-password')} />;
  }

  const header = (
    <header className="sa-header">
      <div className="sa-header-left">
        <span
          className="sa-logo"
          onClick={() => navigate(scopedSchoolId ? 'school-dashboard' : 'dashboard', scopedSchoolId || null)}
          style={{ cursor: 'pointer' }}
        >
          {scopedSchoolId ? 'Car-Pool School Admin' : 'Car-Pool Super Admin'}
        </span>
      </div>
      <div className="sa-header-right">
        <span className="sa-user">{user.name || user.email}</span>
        <button className="sa-btn-logout" onClick={handleLogout}>
          Log out
        </button>
      </div>
    </header>
  );

  const schoolShellConfig = (() => {
    switch (page) {
      case 'school-dashboard':
        return {
          schoolId: pageData,
          activePage: 'dashboard',
          breadcrumb: 'Dashboard',
          headerRight: (
            <DismissalStateControl
              active={getDismissalActive(pageData)}
              onToggle={() => handleDismissalToggle(pageData)}
              interactive={dismissalToggleInteractive}
            />
          ),
          content: (
            <SchoolDashboard
              bare
              token={user.token}
              navigate={navigate}
              schoolId={pageData}
              user={user}
              dismissalActive={getDismissalActive(pageData)}
              onDismissalActiveChange={(nextValue) => setDismissalActive(pageData, nextValue)}
              onStartDismissal={handleStartDismissal}
              onEndDismissal={handleEndDismissal}
            />
          ),
        };
      case 'dismissal-console':
        return {
          schoolId: pageData,
          activePage: 'dismissal',
          breadcrumb: 'Dismissal Console',
          headerRight: (
            <DismissalStateControl
              active={getDismissalActive(pageData)}
              onToggle={() => handleDismissalToggle(pageData)}
              interactive={dismissalToggleInteractive}
            />
          ),
          content: (
            <DismissalConsole
              bare
              token={user.token}
              navigate={navigate}
              schoolId={pageData}
              user={user}
              dismissalActive={getDismissalActive(pageData)}
              onDismissalActiveChange={(nextValue) => setDismissalActive(pageData, nextValue)}
            />
          ),
        };
      case 'pickup-zones':
        return {
          schoolId: pageData?.schoolId,
          activePage: 'pickup-zones',
          breadcrumb: 'Pickup Zones',
          content: (
            <PickupZonesPage
              bare
              token={user.token}
              navigate={navigate}
              schoolId={pageData?.schoolId}
              focusBuilder={pageData?.focusBuilder}
              user={user}
            />
          ),
        };
      case 'gps-diagnostics':
        return {
          schoolId: pageData,
          activePage: 'gps-diagnostics',
          breadcrumb: 'GPS Diagnostics',
          content: <ParentLocationDiagnosticsPage token={user.token} schoolId={pageData} />,
        };
      case 'messages':
        return {
          schoolId: pageData,
          activePage: 'messages',
          breadcrumb: 'Messages',
          content: <SchoolMessagesPage token={user.token} schoolId={pageData} user={user} />,
        };
      case 'push-notifications':
        return {
          schoolId: pageData,
          activePage: 'push-notifications',
          breadcrumb: 'Push Notifications',
          content: <PushNotificationsPage token={user.token} schoolId={pageData} user={user} />,
        };
      case 'calendar':
        return {
          schoolId: pageData,
          activePage: 'calendar',
          breadcrumb: 'Calendar',
          content: <CalendarPage bare token={user.token} navigate={navigate} schoolId={pageData} user={user} />,
        };
      case 'carpool-schedule':
        return {
          schoolId: pageData,
          activePage: 'carpool-schedule',
          breadcrumb: 'Carpool Schedule',
          content: <CarpoolSchedulePage bare token={user.token} navigate={navigate} schoolId={pageData} user={user} />,
        };
      case 'settings':
        return {
          schoolId: pageData,
          activePage: 'settings',
          breadcrumb: 'System Preferences',
          content: <SystemPreferencesPage bare token={user.token} navigate={navigate} schoolId={pageData} user={user} />,
        };
      case 'parents': {
        const parentsSchoolId = pageData?.schoolId ?? pageData;
        return {
          schoolId: parentsSchoolId,
          activePage: 'parents',
          breadcrumb: 'Parents',
          content: (
            <ParentList
              bare
              token={user.token}
              navigate={navigate}
              schoolId={parentsSchoolId}
              selectParentId={pageData?.selectParentId ?? null}
              user={user}
            />
          ),
        };
      }
      case 'drivers': {
        const driversSchoolId = pageData?.schoolId ?? pageData;
        return {
          schoolId: driversSchoolId,
          activePage: 'drivers',
          breadcrumb: 'Drivers',
          content: (
            <DriverList
              token={user.token}
              navigate={navigate}
              schoolId={driversSchoolId}
              selectDriverId={pageData?.selectDriverId ?? null}
            />
          ),
        };
      }
      case 'staff': {
        const staffSchoolId = pageData?.schoolId ?? pageData;
        return {
          schoolId: staffSchoolId,
          activePage: 'staff',
          breadcrumb: 'Staff',
          content: (
            <StaffList
              bare
              token={user.token}
              navigate={navigate}
              schoolId={staffSchoolId}
              selectStaffId={pageData?.selectStaffId ?? null}
              user={user}
            />
          ),
        };
      }
      case 'students':
        return {
          schoolId: pageData?.schoolId ?? pageData,
          activePage: 'students',
          breadcrumb: 'Students',
          content: (
            <StudentList
              bare
              token={user.token}
              navigate={navigate}
              schoolId={pageData?.schoolId ?? pageData}
              selectStudentId={pageData?.selectStudentId ?? null}
              user={user}
            />
          ),
        };
      case 'buses':
        return {
          schoolId: pageData,
          activePage: 'buses',
          breadcrumb: 'Buses',
          content: <BusManagementPage token={user.token} schoolId={pageData} user={user} />,
        };
      case 'visitors': {
        const visitorsSchoolId = pageData?.schoolId ?? pageData;
        return {
          schoolId: visitorsSchoolId,
          activePage: 'visitors',
          breadcrumb: 'Visitors',
          content: (
            <VisitorsPage
              token={user.token}
              schoolId={visitorsSchoolId}
              selectVisitId={pageData?.selectVisitId ?? null}
            />
          ),
        };
      }
      case 'field-trip-forms':
        return {
          schoolId: pageData,
          activePage: 'field-trip-forms',
          breadcrumb: 'Field Trip Forms',
          content: <FieldTripFormsPage token={user.token} schoolId={pageData} />,
        };
      case 'import-review':
        return {
          schoolId: pageData,
          activePage: 'students',
          breadcrumb: 'Import Review',
          content: (
            <ImportReviewPage
              bare
              token={user.token}
              navigate={navigate}
              schoolId={pageData}
              user={user}
            />
          ),
        };
      default:
        return null;
    }
  })();

  if (schoolShellConfig) {
    return (
      <SchoolLayout
        token={user.token}
        navigate={navigate}
        schoolId={schoolShellConfig.schoolId}
        user={user}
        onLogout={handleLogout}
        activePage={schoolShellConfig.activePage}
        breadcrumb={schoolShellConfig.breadcrumb}
        headerRight={schoolShellConfig.headerRight}
      >
        {schoolShellConfig.content}
      </SchoolLayout>
    );
  }

  return (
    <div className="sa-app">
      {header}
      <main className="sa-main">
        {page === 'dashboard' && (
          <Dashboard token={user.token} navigate={navigate} />
        )}
        {page === 'school-create' && (
          <SchoolForm token={user.token} navigate={navigate} />
        )}
        {page === 'school-edit' && (
          <SchoolForm
            token={user.token}
            navigate={navigate}
            school={pageData?.school}
            schoolId={pageData?.schoolId}
          />
        )}
        {page === 'school-detail' && (
          <SchoolDetail token={user.token} navigate={navigate} schoolId={pageData} />
        )}
        {page === 'staff-create' && (
          <StaffForm token={user.token} navigate={navigate} schoolId={pageData} user={user} />
        )}
        {page === 'staff-edit' && (
          <StaffForm
            token={user.token}
            navigate={navigate}
            user={user}
            staff={pageData?.staff}
            staffId={pageData?.staffId}
            schoolId={pageData?.schoolId}
          />
        )}
        {page === 'student-create' && (
          <StudentForm token={user.token} navigate={navigate} schoolId={pageData} user={user} />
        )}
        {page === 'student-edit' && (
          <StudentForm
            token={user.token}
            navigate={navigate}
            user={user}
            student={pageData?.student}
            studentId={pageData?.studentId}
            schoolId={pageData?.schoolId}
          />
        )}
        {page === 'parent-create' && (
          <ParentForm token={user.token} navigate={navigate} schoolId={pageData} user={user} />
        )}
        {page === 'parent-edit' && (
          <ParentForm
            token={user.token}
            navigate={navigate}
            user={user}
            parent={pageData?.parent}
            parentId={pageData?.parentId}
            schoolId={pageData?.schoolId}
          />
        )}
        {page === 'driver-edit' && (
          <ParentForm
            token={user.token}
            navigate={navigate}
            user={user}
            parent={pageData?.driver}
            parentId={pageData?.driverId}
            schoolId={pageData?.schoolId}
            returnPage="drivers"
          />
        )}
        {page === 'parent-detail' && (
          <ParentDetail token={user.token} navigate={navigate} user={user} parentId={pageData?.parentId} schoolId={pageData?.schoolId} />
        )}
        {page === 'simulate' && (
          <Simulate token={user.token} navigate={navigate} />
        )}
        {page === 'carpool-replay' && (
          <CarpoolReplayPage token={user.token} navigate={navigate} />
        )}
        {page === 'server-health' && (
          <ServerHealth token={user.token} navigate={navigate} />
        )}
        {page === 'expo-previews' && (
          <ExpoPreviews token={user.token} navigate={navigate} />
        )}
        {page === 'csv-import' && (
          <CsvImport token={user.token} navigate={navigate} />
        )}
      </main>
    </div>
  );
}
